import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useAsset } from '~/techstyle-shared/react-assets';
import {
  mobile,
  useBreakpoint,
  Video as BaseVideo,
} from '~/techstyle-shared/react-components';

import {
  parseMediaPlayerConfigFromAsset,
  parseVideoIdInput,
} from '../../utils/assetHelper';
import usePromoTracking from '../../utils/usePromoTracking';
import TextNode from '../TextNode';
import TrackedAssetContainer from '../TrackedAssetContainer';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  position: absolute;
  ${({ $textPosition }) =>
    $textPosition === 'bottom_left' &&
    css`
      left: 2%;
      bottom: 4%;
    `}
`;
const Title = styled(TextNode)`
  color: ${({ theme }) => theme.colors.white};
`;

const Description = styled(TextNode)`
  color: ${({ theme }) => theme.colors.white};
`;

const BackgroundImage = styled(TrackedAssetContainer)`
  width: 100%;
  ${mobile`
    img {
      object-fit: cover;
    }
  `}
`;

const AssetNodeTypes = {
  /**
   * The asset container name to display
   */
  container: PropTypes.string,
  customFields: PropTypes.object,
  description: PropTypes.object,
  /**
   * The mobile asset container name to display
   */
  mobileContainer: PropTypes.string,
  title: PropTypes.object,
};

const AssetNodeShape = PropTypes.shape(AssetNodeTypes);

function AssetNode({
  container,
  mobileContainer,
  title,
  description,
  customFields,
  ...props
}) {
  const { isMobile } = useBreakpoint();
  const assetRef = useRef(null);

  const { triggerPromoTracking } = usePromoTracking();

  const renderedContainer =
    isMobile && mobileContainer?.length ? mobileContainer : container;

  const { data } = { ...useAsset(renderedContainer) };

  if (!data) {
    return null;
  }

  const { assets = [] } = data;

  if (!assets || !assets.length) {
    return null;
  }
  const asset = assets[0];
  const handlePromoClicked = () => {
    triggerPromoTracking(asset);
  };

  // check for a video id
  const { videoId, videoAspectRatio } = parseVideoIdInput({ asset });

  if (!asset.options.htmlText) {
    // only show if there's an asset in the container
    if (!asset.imageFilename && !videoId) {
      return null;
    }
  }

  if (videoId) {
    const playerCustomConfig = parseMediaPlayerConfigFromAsset({ asset });

    return (
      <Wrapper
        name={renderedContainer}
        aspectRatio="auto"
        background
        onClick={handlePromoClicked}
        data-builder-component-type="asset"
        {...props}
      >
        <TrackedAssetContainer
          name={renderedContainer}
          url={videoId && BaseVideo.getUrlFromVideoId(videoId)}
          aspectRatio={videoId && videoAspectRatio}
          hasControls={false}
          config={playerCustomConfig}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper
      ref={assetRef}
      onClick={handlePromoClicked}
      data-builder-component-type="asset"
    >
      <BackgroundImage
        name={renderedContainer}
        aspectRatio="auto"
        background
        {...props}
      />
      {customFields && customFields.text_position && (
        <TextWrapper $textPosition={customFields.text_position}>
          {title && (
            <Title
              defaultMessage={title.text}
              messageId={title.text_rb}
              containerStyle={title.container_style}
              textStyle={title.text_style}
            />
          )}
          {description && (
            <Description
              defaultMessage={description.text}
              messageId={description.text_rb}
              containerStyle={description.container_style}
              textStyle={description.text_style}
            />
          )}
        </TextWrapper>
      )}
    </Wrapper>
  );
}

AssetNode.propTypes = AssetNodeTypes;

export default AssetNode;
export { AssetNodeTypes, AssetNodeShape };
